import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Icon, Button, Input, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import GenerateSkeletonComponent from './GenerateSkeletonComponent';
import DynamicContentLink from './DynamicContentLink';
import {
  isDynamicContent,
  contentPreviewSource,
  isVideoPreview,
  contentResolutionLabel
} from '../../../services/contents';
import Colors from '../../../themes/Colors';
import ModalContentHolder from '../styles/ModalContentStyle';
import ScreensGroupHolder from '../../features/campaignManager/styles/screensGroupStyle';

const MAX_FRAME_WIDTH = 340;
const MAX_FRAME_HEIGHT = 260;

const InfoRow = ({ icon, label, children }) => (
  <div className="infoCol">
    <Icon className="infoIcon" type={icon} />
    <div className="infoText">
      <div className="label">{label}</div>
      {children}
    </div>
  </div>
);

// `onChangeName` enables the edit button on the name; without it the modal stays read-only.
// `relation` renders the ad inside the screen's own aspect ratio instead of a plain preview.
const ModalContentComponent = ({
  visible = false,
  onCancel,
  content,
  relation,
  onChangeName,
  disabled = false
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);

  useEffect(() => {
    if (visible) return;

    setLoading(true);
    setEditingName(false);
  }, [visible]);

  // A dynamic ad is an HTML page served from an external URL: never rendered
  // here, only its uploaded preview (a still or a clip) plus a link out.
  const isDynamic = !!content && isDynamicContent(content.type);
  const previewSource = isDynamic ? contentPreviewSource(content) : null;
  // With no preview there is no media, so no load event will ever arrive and the
  // skeleton would spin forever.
  const waitsForMedia = !isDynamic || !!previewSource;

  const isExactSize = relation && !content.differentResolution;
  const screenAspectRatio = relation ? relation.resolution_width / relation.resolution_height : null;
  const maxAspectRatio = MAX_FRAME_WIDTH / MAX_FRAME_HEIGHT;
  const frameWidth = screenAspectRatio
    ? (screenAspectRatio > maxAspectRatio ? MAX_FRAME_WIDTH : MAX_FRAME_HEIGHT * screenAspectRatio)
    : null;
  const frameHeight = screenAspectRatio
    ? (screenAspectRatio > maxAspectRatio ? MAX_FRAME_WIDTH / screenAspectRatio : MAX_FRAME_HEIGHT)
    : null;

  return (
    <>
      {visible && (
        <Modal
          title={t('Content data')}
          visible={visible}
          onCancel={onCancel}
          footer={[
            <Button key="ok" type="primary" onClick={onCancel}>
              {t('Accept')}
            </Button>
          ]}
          width={relation ? 760 : 800}
        >
          <ModalContentHolder>
            {content && (
              <Row
                type="flex"
                justify="center"
                align="middle"
                style={relation ? { minHeight: MAX_FRAME_HEIGHT } : undefined}
              >
                <Col
                  xs={14}
                  sm={14}
                  style={relation ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' } : undefined}
                >
                  {relation ? (
                    <>
                      <ScreensGroupHolder>
                        <div className="screen" style={{ width: frameWidth, height: frameHeight, borderRadius: 4 }}>
                          {isDynamic ? (
                            previewSource ? (
                              isVideoPreview(content) ? (
                                <video
                                  className={isExactSize ? 'screenPreviewFilled' : 'screenPreview'}
                                  src={previewSource}
                                  autoPlay
                                  muted
                                  loop
                                  onLoadedData={() => setLoading(false)}
                                />
                              ) : (
                                <img
                                  className={isExactSize ? 'screenPreviewFilled' : 'screenPreview'}
                                  src={previewSource}
                                  alt=""
                                  onLoad={() => setLoading(false)}
                                />
                              )
                            ) : (
                              <Icon type="global" style={{ fontSize: 32, color: Colors.primaryAccent }} />
                            )
                          ) : content.type === 'video' ? (
                            <video
                              className={isExactSize ? 'screenPreviewFilled' : 'screenPreview'}
                              src={content.file}
                              controls
                              autoPlay
                              onLoadedData={() => setLoading(false)}
                            />
                          ) : (
                            <img
                              className={isExactSize ? 'screenPreviewFilled' : 'screenPreview'}
                              src={content.file}
                              alt=""
                              onLoad={() => setLoading(false)}
                            />
                          )}
                        </div>
                      </ScreensGroupHolder>
                      {content.differentResolution && (
                        <div style={{ color: '#faad14', marginTop: 12 }}>
                          <Icon type="warning" /> {t('Tolerance applied')}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Only hidden while media is genuinely loading: with no preview no load
                          event ever arrives, so the icon must not be hidden. */}
                      <div className="contentCardContent" hidden={waitsForMedia && loading}>
                        {isDynamic ? (
                          previewSource ? (
                            isVideoPreview(content) ? (
                              <video
                                className="appear-animation-without-movement"
                                src={previewSource}
                                width={350}
                                autoPlay
                                muted
                                loop
                                onLoadedData={() => setLoading(false)}
                              />
                            ) : (
                              <img
                                className="appear-animation-without-movement"
                                src={previewSource}
                                alt=""
                                width={350}
                                onLoad={() => setLoading(false)}
                              />
                            )
                          ) : (
                            // No media to give the box a height, so it takes the
                            // skeleton's instead of collapsing around the icon.
                            <div
                              style={{
                                height: 202,
                                background: Colors.surfaceMuted,
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Icon type="global" style={{ fontSize: 32, color: Colors.primaryAccent }} />
                            </div>
                          )
                        ) : content.file.endsWith('.jpg') ||
                        content.file.endsWith('.png') ||
                        content.file.endsWith('.gif') ? (
                          <img
                            className="appear-animation-without-movement"
                            src={content.file}
                            alt=""
                            width={350}
                            onLoad={() => setLoading(false)}
                          />
                        ) : (
                          <video
                            className="appear-animation-without-movement"
                            src={content.file}
                            width={350}
                            controls
                            autoPlay
                            onLoadedData={() => setLoading(false)}
                            preload="metadata"
                          />
                        )}
                      </div>
                      {waitsForMedia && loading && (
                        <GenerateSkeletonComponent count={1} height={202} width="100%" />
                      )}
                    </>
                  )}
                  {isDynamic && (
                    <div style={{ marginTop: 5, textAlign: 'center' }}>
                      <DynamicContentLink url={content.file} />
                    </div>
                  )}
                </Col>
                <Col xs={10} sm={10}>
                  <div className="content-info-bunlde">
                    <div className="infoTitle">{t('General information')}</div>
                    <InfoRow icon="tag" label={t('Name')}>
                      {editingName ? (
                        <Input
                          autoFocus
                          value={content.name}
                          onChange={e => onChangeName(e.target.value)}
                          onBlur={() => setEditingName(false)}
                          onPressEnter={() => setEditingName(false)}
                        />
                      ) : (
                        <div className="value">
                          {content.name}
                          {onChangeName && !disabled && (
                            <Tooltip title={t('Edit name')}>
                              <Icon
                                className="editIcon"
                                type="edit"
                                onClick={() => setEditingName(true)}
                              />
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </InfoRow>
                    {!isDynamic && (
                      <InfoRow icon="fullscreen" label={t('Resolution')}>
                        <div className="value">
                          {content.width} x {content.height}
                        </div>
                      </InfoRow>
                    )}
                    {relation && (
                      <InfoRow icon="desktop" label={t('Screen resolution')}>
                        <div className="value">
                          {relation.resolution_width} x {relation.resolution_height}
                        </div>
                      </InfoRow>
                    )}
                    <InfoRow
                      icon={
                        isDynamic
                          ? 'global'
                          : content.type === 'video'
                          ? 'video-camera'
                          : 'file-image'
                      }
                      label={t('Type')}
                    >
                      <div className="value">
                        {isDynamic ? t('DCO') : t(content.type === 'video' ? 'Video' : 'Image')}
                      </div>
                    </InfoRow>
                    {content.type === 'video' && (
                      <InfoRow icon="clock-circle" label={t('Duration')}>
                        <div className="value">
                          {Math.floor(content.length / 1000)} {t('secs')}
                        </div>
                      </InfoRow>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </ModalContentHolder>
        </Modal>
      )}
    </>
  );
};

export default ModalContentComponent;
